import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/lab';
import DateAdapter from '@mui/lab/AdapterMoment';
import { IHeader } from 'components/Header';
import Header from 'components/Header/Header';
import { useLocation, useNavigate } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { IReducer } from 'Reducers/IReducer';
import { IFormikSampleFood, IPersistSampleKSZProps } from 'pages/Sample/PersistSampleKSZ';
import { searchCustomer } from 'actions/CustomerActions';
import {
  searchMasterList,
  searchTestGroupList,
  createSample,
  getSampleById,
  updateSample,
  searchReference,
} from 'actions/LaboratoryActions';
import { getUserList } from 'actions/UserAction';
import { SearchDialog } from 'components/SearchDialog';
import { ICustomer, IMaster, IReference, ITestGroup, IUser } from 'exactt-types/lib';
import { useFormik } from 'formik';
import useQuery from 'utils/getQueryParams';
import moment from 'moment';
import { CustomFieldWithLabel } from 'components/CustomFieldWithLabel';
import { createSampleSchema } from 'FormSchema/CreateSampleSchema';
import { handleChangeWhiteSpace } from 'utils/removeWhiteSpaces';
import { normalizeDateForApi } from 'utils/date';
import AsyncSelect from 'react-select/async';
import ReactSelect from 'react-select';
import makeAnimated from 'react-select/animated';
const animatedComponents = makeAnimated();

const getEntityId = (entity: any) => entity?._id || entity?.id || (typeof entity === 'string' ? entity : '');

const buildCustomerLabel = (customer: any) => {
  if (!customer) return '';
  const name = customer.name || customer.customerName || customer.label || '';
  const address = customer.address?.[0];
  const addressText = [address?.line1, address?.line2, address?.city, address?.state].filter(Boolean).join(', ');
  return addressText ? `${name} (${addressText})` : name;
};

export default function _PersistSampleKSZ(props: IPersistSampleKSZProps) {
  const navigate = useNavigate();
  const location = useLocation();
  let query = useQuery();
  let sampleId = query.get('id');
  const copySampleData = (location.state as any)?.copySampleData;
  const laboratoryId = props.laboratory?.details?._id || '';
  const isCopyMode = Boolean(copySampleData) && !sampleId;

  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchModal, setCustomerSearchModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<ICustomer | null>(null);
  const [referenceSearchQuery, setReferenceSearchQuery] = useState('');
  const [referenceSearchModal, setReferenceSearchModal] = useState(false);
  const [selectedReference, setSelectedReference] = useState<IReference | null>(null);
  const [masterSearchQuery, setMasterSearchQuery] = useState('');
  const [masterSearchModal, setMasterSearchModal] = useState(false);
  const [selectedMaster, setSelectedMaster] = useState<IMaster | null>(null);
  const [testGroupSearchQuery, setTestGroupSearchQuery] = useState('');
  const [testGroupSearchModal, setTestGroupSearchModal] = useState(false);
  const [selectedTestGroup, setSelectedTestGroup] = useState<ITestGroup | null>(null);

  const formik: IFormikSampleFood = useFormik({
    initialValues: createSampleSchema.initialValues,
    validationSchema: createSampleSchema.validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const samplePayload = {
        ...values,
        collectionDate: normalizeDateForApi(values.collectionDate) || values.collectionDate,
        sampleCollectionDate: normalizeDateForApi((values as any).sampleCollectionDate),
      };

      try {
        if (sampleId) {
          await props.updateSample({
            ...samplePayload,
            laboratoryId,
            _id: sampleId,
            master: selectedMaster?._id || '',
            formType: 'FOOD',
            sampleReceivedBy: values.sampleReceivedMode || '',
            dispatchMode: '',
          } as any);
          navigate('/laboratory/samples/sampleDetails?id=' + sampleId);
        } else {
          await props.createSample({
            ...samplePayload,
            laboratoryId,
            master: selectedMaster?._id || '',
            formType: 'FOOD',
            sampleReceivedBy: '',
            sampleReceivedMode: values.sampleReceivedMode || '',
            dispatchMode: '',
          } as any);
          navigate('/laboratory/samples');
        }
      } catch (err) {
        console.error(err);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleChange = (event: any) => {
    const { value } = event.target;
    const length = event.target.value.length;
    if (value[length - 1] === ' ' && value[length - 2] === ' ') {
      event.target.value = handleChangeWhiteSpace(value);
    }
    formik.handleChange(event);
  };

  useEffect(() => {
    if (sampleId) {
      props.getSampleById(laboratoryId, sampleId);
    }
    // eslint-disable-next-line
  }, [sampleId, laboratoryId]);

  useEffect(() => {
    const sourceSample: any = sampleId ? props.sampleDetails : copySampleData;
    const hasSourceSample = Boolean(copySampleData || sourceSample?._id || sourceSample?.customer);

    if (hasSourceSample) {
      setSelectedCustomer(
        isCopyMode
          ? null
          : typeof sourceSample.customer === 'object'
            ? (sourceSample.customer as ICustomer)
            : ({
              _id: getEntityId(sourceSample.customer),
              name: sourceSample.customerName || sourceSample.bookingParty || '',
            } as ICustomer)
      );
      setSelectedTestGroup(sourceSample.testGroup as ITestGroup);
      setSelectedMaster(sourceSample.master as IMaster);

      formik.setValues({
        ...createSampleSchema.initialValues,
        // !dates are set to new Date() because of the error in date format
        batchNo: sourceSample.batchNo || '',
        batchSize: sourceSample.batchSize || '',
        brandName: sourceSample.brandName || '',
        collectionBy: (sourceSample.collectionBy as any) || '',
        collectionDate: normalizeDateForApi(sourceSample.collectionDate) || new Date(),
        sampleCollectionDate: normalizeDateForApi(sourceSample.sampleCollectionDate),
        description: sourceSample.description || '',
        discount: sourceSample.discount || 0,
        dueDate: sourceSample.dueDate || new Date(),
        drugLicNo: sourceSample.drugLicNo || '',
        envCondition: sourceSample.envCondition || '',
        expDate: sourceSample.expDate || new Date(),
        gst: sourceSample.gst || 0,
        hod: (sourceSample.hod as IUser)?._id || '',
        igst: sourceSample.igst || false,
        labDueDate: sourceSample.labDueDate || new Date(),
        mfgDate: sourceSample.mfgDate || new Date(),
        sampleReceivedMode: sourceSample.sampleReceivedMode || '',
        otherInformation: sourceSample.otherInformation || '',
        name: sourceSample.name,
        paidAmount: sourceSample.paidAmount || 0,
        foodadditive: sourceSample.foodadditive || '',
        parameters:
          sourceSample.parameters?.map((i: any) => ({
            ...i,
            department: i.department && typeof i.department === 'object' ? i.department._id : i.department || '',
          })) || [],
        rate: sourceSample.rate || 0,
        sac: sourceSample.sac || '',
        sampleCode: sourceSample.sampleCode || '',
        sampleCondition: sourceSample.sampleCondition || '',
        samplePacking: sourceSample.samplePacking || '',
        sampleQty: sourceSample.sampleQty || '',
        samplingMethod: sourceSample.samplingMethod || '',
        servingSize: sourceSample.servingSize || '',
        supplier: sourceSample.supplier || '',
        testGroup: getEntityId(sourceSample.testGroup),
        type: sourceSample.type || '',
        additionalFields: sourceSample.additionalFields || {},
        customer: isCopyMode ? '' : getEntityId(sourceSample.customer),
        reference: getEntityId(sourceSample.reference),
        genericName: sourceSample.genericName,
        manufacture: sourceSample.manufacture || '',
        marking: sourceSample.marking || '',
        paymentDueDate: sourceSample.paymentDueDate || new Date(),
        reviewedBy: (sourceSample.reviewedBy as any)?._id || '',
        authorizedSignature1: (sourceSample.authorizedSignature1 as IUser)?._id || '',
        authorizedSignature2: (sourceSample.authorizedSignature2 as IUser)?._id || '',
        nutritionalInformation: sourceSample.nutritionalInformation || '',
        vagNonVegDetails: sourceSample.vagNonVegDetails || '',
        nameAndAddressOfImporter: sourceSample.nameAndAddressOfImporter || '',
        countryOfOrigin: sourceSample.countryOfOrigin || '',
        testMethod: sourceSample.testMethod || '',
        nameOfPlantSection: sourceSample.nameOfPlantSection || '',
        purposeOfMonitoring: sourceSample.purposeOfMonitoring || '',
        emissionSourceMonitor: sourceSample.emissionSourceMonitor || '',
        productManufacturedSamplingTime: sourceSample.productManufacturedSamplingTime || '',
        nameOfAuthorizedPerson: sourceSample.nameOfAuthorizedPerson || '',
        controlMeasures: sourceSample.controlMeasures || '',
        authorizedPersonAddress: sourceSample.authorizedPersonAddress || '',
        recoveryOfMaterial: sourceSample.recoveryOfMaterial || '',
        sealedCoverByThe: sourceSample.sealedCoverByThe || '',
        sealOnContainerBISSeal: sourceSample.sealOnContainerBISSeal || '',
        generalSensory: sourceSample.generalSensory || '',
        CHACardHolder: sourceSample.CHACardHolder || '',
        CHSRegnNo: sourceSample.CHSRegnNo || '',
        fugitiveEmission: sourceSample.fugitiveEmission || '',
        stackTemp: sourceSample.stackTemp || '',
        stackVelocity: sourceSample.stackVelocity || '',
        sampleLocation: sourceSample.sampleLocation || '',
        dateAndPlaceOfCollectionOfSample: sourceSample.dateAndPlaceOfCollectionOfSample || '',
        ambientTemperature: sourceSample.ambientTemperature || '',
        srNo: sourceSample.srNo || '',
        sealsFixedNo: sourceSample.sealsFixedNo || '',
        conformsOrNon: sourceSample.conformsOrNon || '',
        serviceMode: sourceSample.serviceMode || '',
        foundTheSampleToBe: sourceSample.foundTheSampleToBe || '',
        sampleStatus: sourceSample.sampleStatus || '',
        statusReasons: sourceSample.statusReasons || '',

        // ! new added fields
        regulationNo: sourceSample.regulationNo || '',
        beNo: sourceSample.beNo || '',
        sealOnContainer: sourceSample.sealOnContainer || '',
        dateOfDispatch: sourceSample.dateOfDispatch || new Date(),
        testMemoAndDate: sourceSample.testMemoAndDate || '',
        sampleDescription: sourceSample.sampleDescription || '',
        physicalAppearance: sourceSample.physicalAppearance || '',
        label: sourceSample.label || '',
        fassiLogoAndLicenseNo: sourceSample.fassiLogoAndLicenseNo || '',
        listOfIngratiation: sourceSample.listOfIngratiation || '',
        parametersMonitors: sourceSample.parametersMonitors || '',
      });
    }
    // eslint-disable-next-line
  }, [copySampleData, isCopyMode, props.sampleDetails]);

  useEffect(() => {
    props.searchCustomer(laboratoryId, { page: 1, size: 10, query: customerSearchQuery });
    // eslint-disable-next-line
  }, [customerSearchQuery]);

  useEffect(() => {
    props.searchMasterList(laboratoryId, { page: 1, size: 10, query: masterSearchQuery });
    // eslint-disable-next-line
  }, [masterSearchQuery]);

  useEffect(() => {
    props.searchTestGroupList(laboratoryId, { page: 1, size: 10, query: testGroupSearchQuery });
    // eslint-disable-next-line
  }, [testGroupSearchQuery]);

  useEffect(() => {
    props.searchReference(laboratoryId, { page: 1, size: 10, query: referenceSearchQuery });
    // eslint-disable-next-line
  }, [referenceSearchQuery]);

  useEffect(() => {
    if (!selectedCustomer && formik.values.customer) {
      const foundCustomer = props.searchCustomerList.find((item) => item._id === formik.values.customer);
      if (foundCustomer) setSelectedCustomer(foundCustomer);
    }
  }, [formik.values.customer, props.searchCustomerList, selectedCustomer]);

  const breadcrumbsList: IHeader['breadcrumbsList'] = [
    {
      label: 'Dashboard',
      onClick: () => navigate('/'),
    },
    {
      label: 'Samples',
      onClick: () => navigate('/laboratory/samples'),
    },
  ];

  const promiseCustomerOptions = async (inputValue: string) => {
    const list = await props.searchCustomer(laboratoryId, { page: 1, size: 10, query: inputValue });
    console.log('list -> ', list.data.customers);

    return list.data.customers.map((i: any) => ({
      value: i._id,
      label: `${i.name} | (${i.address?.[0]?.line1 || ''}, ${i.address?.[0]?.line2 || ''}, ${i.address?.[0]?.city || ''
        }, ${i.address?.[0]?.state || ''})`,
      option: i,
    }));
  };

  const promiseReferenceOptions = async (inputValue: string) => {
    const list = await props.searchReference(laboratoryId, { page: 1, size: 10, query: inputValue });
    return list.data.references.map((i: any) => ({
      value: i._id,
      label: i.name,
      option: i,
    }));
  };

  const promiseTestGroupOptions = async (inputValue: string) => {
    const list = await props.searchTestGroupList(laboratoryId, { page: 1, size: 10, query: inputValue });
    return list.data.testGroups.map((i: any) => ({
      value: i._id,
      label: i.name,
      option: i,
    }));
  };
  const handleSelectAllParameter = () => {
    if (selectedMaster) {
      const parameters = selectedMaster?.parameters;
      if (parameters?.length) {
        formik.setFieldValue('parameters', parameters);
      }
    } else {
      if (formik.values.parameters.length === 0) {
        formik.setFieldError('parameters', 'Please select a master');
      }
    }
  };
  const gstAmount = formik.values.rate * (formik.values.gst / 100);
  const totalRate = formik.values.rate + gstAmount;
  const discountAmount = totalRate * (formik.values.discount / 100);
  const total = totalRate - discountAmount;
  const remainingTotal = total - formik.values.paidAmount;

  console.log(
    ' data-> ',
    props.searchCustomerList.map((i) => ({
      label: `${i.name} | (${i.address?.[0]?.line1 || ''}, ${i.address?.[0]?.line2 || ''})`,
      value: i._id,
    }))
  );

  return (
    <Box>
      <Header breadcrumbsList={breadcrumbsList} title="Sample Entry" />
      <Box p={2} pt={10}>
        <Grid item xs={12} gap={1}>
          <Grid item xs={12}>
            <CustomFieldWithLabel width={120} label="Booking Party">
              <AsyncSelect
                loadOptions={promiseCustomerOptions}
                value={
                  selectedCustomer
                    ? {
                      value: selectedCustomer._id,
                      label: buildCustomerLabel(selectedCustomer),
                    }
                    : null
                }
                defaultOptions={
                  props.searchCustomerList?.length
                    ? props.searchCustomerList.map((i) => ({
                      label: `${i.name} | (${i.address?.[0]?.line1 || ''}, ${i.address?.[0]?.line2 || ''})`,
                      value: i._id,
                    }))
                    : []
                }
                onChange={(newValue: any) => {
                  const findCustomer = props.searchCustomerList.find((i) => i._id === newValue?.value);
                  if (findCustomer) setSelectedCustomer(findCustomer);
                  formik.setFieldValue('customer', newValue?.value || '');
                }}
              />
            </CustomFieldWithLabel>
            {formik.touched.customer && formik.errors.customer}
          </Grid>
        </Grid>
        <Grid container spacing={1} mt={1}>
          <Grid item xs={6}>
            <Grid container spacing={1} mt={1}>
              <Grid item xs={12}>
                <Typography style={{ textTransform: 'uppercase' }} fontWeight={'500'} fontSize={14}>
                  Basic Details
                </Typography>
                <Divider />
              </Grid>
              <Grid item xs={12} gap={1}>
                <Grid item xs={12}>
                  {/* <CustomFieldWithLabel width={120} label="Reference by">
                    <AsyncSelect
                      loadOptions={promiseReferenceOptions}
                      value={{ value: selectedReference?._id, label: selectedReference?.name }}
                      defaultOptions={(props.referenceList || []).map((i) => ({
                        label: i.name,
                        value: i._id,
                      }))}
                      onChange={(newValue: any) => {
                        const findRef = (props.referenceList || []).find((i) => i._id === newValue?.value);
                        if (findRef) setSelectedReference(findRef);
                        formik.setFieldValue('reference', newValue?.value || '');
                      }}
                    />
                  </CustomFieldWithLabel>
                  {formik.touched.reference && formik.errors.reference} */}
                </Grid>
              </Grid>
              <Grid item xs={12} gap={1} style={{ display: 'flex' }}>
                <Grid item xs={12}>
                  <CustomFieldWithLabel width={120} label="Test Group">
                    <AsyncSelect
                      loadOptions={promiseTestGroupOptions}
                      value={{ value: selectedTestGroup?._id, label: selectedTestGroup?.name }}
                      defaultOptions={(props.testGroupsList || []).map((i) => ({
                        label: i.name,
                        value: i._id,
                      }))}
                      onChange={(newValue: any) => {
                        const testGroup = (props.testGroupsList || []).find((i) => i._id === newValue?.value);
                        if (testGroup) setSelectedTestGroup(testGroup);
                        formik.setFieldValue('testGroup', newValue?.value || '');
                      }}
                    />
                    {/* <TextField
                      fullWidth
                      margin="none"
                      required
                      disabled={!!sampleId}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setTestGroupSearchModal(true);
                        }
                      }}
                      value={selectedTestGroup?.name || ''}
                      error={formik.touched.testGroup && formik.errors.testGroup ? true : false}
                      helperText={formik.touched.testGroup && formik.errors.testGroup}
                    /> */}
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Receipt date">
                    <LocalizationProvider
                      dateAdapter={DateAdapter}
                      value={formik.values.collectionDate}
                      error={formik.touched.collectionDate && formik.errors.collectionDate ? true : false}
                      helperText={formik.touched.collectionDate && formik.errors.collectionDate}
                    >
                      <DesktopDatePicker
                        inputFormat="DD/MM/YYYY"
                        // disabled={true}
                        onChange={(value) => {
                          formik.setFieldValue('collectionDate', normalizeDateForApi(value) || formik.values.collectionDate);
                          formik.setFieldTouched('collectionDate', true);
                        }}
                        value={formik.values.collectionDate}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            margin="none"
                            error={formik.touched.collectionDate && formik.errors.collectionDate ? true : false}
                            helperText={formik.touched.collectionDate && formik.errors.collectionDate}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </CustomFieldWithLabel>
                </Grid>

                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Sample Collection Date">
                    <LocalizationProvider
                      dateAdapter={DateAdapter}
                      value={(formik.values as any).sampleCollectionDate}
                      error={Boolean((formik.touched as any).sampleCollectionDate && (formik.errors as any).sampleCollectionDate)}
                      helperText={(formik.touched as any).sampleCollectionDate && (formik.errors as any).sampleCollectionDate}
                    >
                      <DesktopDatePicker
                        inputFormat="DD/MM/YYYY"
                        onChange={(value) => {
                          formik.setFieldValue('sampleCollectionDate', normalizeDateForApi(value));
                          formik.setFieldTouched('sampleCollectionDate', true);
                        }}
                        value={(formik.values as any).sampleCollectionDate}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            margin="none"
                            error={Boolean((formik.touched as any).sampleCollectionDate && (formik.errors as any).sampleCollectionDate)}
                            helperText={(formik.touched as any).sampleCollectionDate && (formik.errors as any).sampleCollectionDate}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>

              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Due date">
                    <LocalizationProvider
                      dateAdapter={DateAdapter}
                      value={formik.values.dueDate}
                      error={formik.touched.dueDate && formik.errors.dueDate ? true : false}
                      helperText={formik.touched.dueDate && formik.errors.dueDate}
                    >
                      <DesktopDatePicker
                        inputFormat="DD/MM/YYYY"
                        onChange={(value) => {
                          formik.setFieldValue('dueDate', moment(value).toDate());
                          formik.setFieldTouched('dueDate', true);
                        }}
                        value={formik.values.dueDate}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required
                            margin="none"
                            fullWidth
                            error={formik.touched.dueDate && formik.errors.dueDate ? true : false}
                            helperText={formik.touched.dueDate && formik.errors.dueDate}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </CustomFieldWithLabel>
                </Grid>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Lab due Date">
                    <LocalizationProvider
                      dateAdapter={DateAdapter}
                      value={formik.values.labDueDate}
                      error={formik.touched.labDueDate && formik.errors.labDueDate ? true : false}
                      helperText={formik.touched.labDueDate && formik.errors.labDueDate}
                    >
                      <DesktopDatePicker
                        inputFormat="DD/MM/YYYY"
                        onChange={(value) => {
                          formik.setFieldValue('labDueDate', moment(value).toDate());
                          formik.setFieldTouched('labDueDate', true);
                        }}
                        value={formik.values.labDueDate}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            margin="none"
                            error={formik.touched.labDueDate && formik.errors.labDueDate ? true : false}
                            helperText={formik.touched.labDueDate && formik.errors.labDueDate}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography style={{ textTransform: 'uppercase', marginTop: '1%' }} fontSize={14}>
                  Sample Details
                </Typography>
                <Divider />
              </Grid>
              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={12}>
                  <CustomFieldWithLabel width={120} label="Sample Name">
                    <TextField
                      fullWidth
                      required
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setMasterSearchModal(true);
                        }
                      }}
                      name="name"
                      onChange={handleChange}
                      value={formik.values.name || ''}
                      error={formik.touched.name && formik.errors.name ? true : false}
                      helperText={formik.touched.name && formik.errors.name}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={12}>
                  <CustomFieldWithLabel width={120} label="Generic Name">
                    <TextField
                      fullWidth
                      required
                      margin="none"
                      value={formik.values.genericName || ''}
                      error={formik.touched.genericName && formik.errors.genericName ? true : false}
                      helperText={formik.touched.genericName && formik.errors.genericName}
                      onChange={handleChange}
                      name={'genericName'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>
              <Grid item xs={12} gap={1} style={{ display: 'flex' }}>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="BE No">
                    <TextField
                      margin="none"
                      fullWidth
                      sx={{ padding: '0px !important' }}
                      value={formik.values.beNo}
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.beNo && formik.errors.beNo ? true : false}
                      helperText={formik.touched.beNo && formik.errors.beNo}
                      name={'beNo'}
                    />
                  </CustomFieldWithLabel>
                </Grid>

                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Seal on Container">
                    <TextField
                      fullWidth
                      margin="none"
                      value={formik.values.sealOnContainer}
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.sealOnContainer && formik.errors.sealOnContainer ? true : false}
                      helperText={formik.touched.sealOnContainer && formik.errors.sealOnContainer}
                      name={'sealOnContainer'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>
              <Grid gap={1} item xs={12} style={{ display: 'flex' }}>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Date of Dispatch">
                    <LocalizationProvider
                      dateAdapter={DateAdapter}
                      value={formik.values.dateOfDispatch}
                      error={formik.touched.dateOfDispatch && formik.errors.dateOfDispatch ? true : false}
                      helperText={formik.touched.dateOfDispatch && formik.errors.dateOfDispatch}
                    >
                      <DesktopDatePicker
                        inputFormat="DD/MM/YYYY"
                        onChange={(value) => {
                          formik.setFieldValue('dateOfDispatch', moment(value).toDate());
                          formik.setFieldTouched('dateOfDispatch', true);
                        }}
                        value={formik.values.dateOfDispatch}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            required
                            margin="none"
                            fullWidth
                            error={formik.touched.dateOfDispatch && formik.errors.dateOfDispatch ? true : false}
                            helperText={formik.touched.dateOfDispatch && formik.errors.dateOfDispatch}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </CustomFieldWithLabel>
                </Grid>

                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Test memo No & Date">
                    <TextField
                      fullWidth
                      margin="none"
                      value={formik.values.testMemoAndDate}
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.testMemoAndDate && formik.errors.testMemoAndDate ? true : false}
                      helperText={formik.touched.testMemoAndDate && formik.errors.testMemoAndDate}
                      name={'testMemoAndDate'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Type of sample">
                    <TextField
                      fullWidth
                      margin="none"
                      value={formik.values.type}
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.type && formik.errors.type ? true : false}
                      helperText={formik.touched.type && formik.errors.type}
                      name={'type'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Regulation No">
                    <TextField
                      fullWidth
                      margin="none"
                      value={formik.values.regulationNo}
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.regulationNo && formik.errors.regulationNo ? true : false}
                      helperText={formik.touched.regulationNo && formik.errors.regulationNo}
                      name={'regulationNo'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={12}>
                  <CustomFieldWithLabel width={120} label="Sample Description">
                    <TextField
                      fullWidth
                      margin="none"
                      value={formik.values.sampleDescription}
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.sampleDescription && formik.errors.sampleDescription ? true : false}
                      helperText={formik.touched.sampleDescription && formik.errors.sampleDescription}
                      name={'sampleDescription'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={12}>
                  <CustomFieldWithLabel width={120} label="Physical Appearance">
                    <TextField
                      fullWidth
                      margin="none"
                      value={formik.values.physicalAppearance}
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.physicalAppearance && formik.errors.physicalAppearance ? true : false}
                      helperText={formik.touched.physicalAppearance && formik.errors.physicalAppearance}
                      name={'physicalAppearance'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={12}>
                  <CustomFieldWithLabel width={120} label="Label">
                    <TextField
                      fullWidth
                      margin="none"
                      value={formik.values.label}
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.label && formik.errors.label ? true : false}
                      helperText={formik.touched.label && formik.errors.label}
                      name={'label'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="FASSI Logo & License No">
                    <TextField
                      fullWidth
                      margin="none"
                      value={formik.values.fassiLogoAndLicenseNo}
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.fassiLogoAndLicenseNo && formik.errors.fassiLogoAndLicenseNo ? true : false}
                      helperText={formik.touched.fassiLogoAndLicenseNo && formik.errors.fassiLogoAndLicenseNo}
                      name={'fassiLogoAndLicenseNo'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="List Of Ingredient">
                    <TextField
                      fullWidth
                      margin="none"
                      value={formik.values.listOfIngratiation}
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.listOfIngratiation && formik.errors.listOfIngratiation ? true : false}
                      helperText={formik.touched.listOfIngratiation && formik.errors.listOfIngratiation}
                      name={'listOfIngratiation'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Nutritional Information">
                    <TextField
                      fullWidth
                      margin="none"
                      value={formik.values.nutritionalInformation}
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.nutritionalInformation && formik.errors.nutritionalInformation ? true : false
                      }
                      helperText={formik.touched.nutritionalInformation && formik.errors.nutritionalInformation}
                      name={'nutritionalInformation'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Veg Non-Veg Details">
                    <TextField
                      fullWidth
                      margin="none"
                      value={formik.values.vagNonVegDetails}
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.vagNonVegDetails && formik.errors.vagNonVegDetails ? true : false}
                      helperText={formik.touched.vagNonVegDetails && formik.errors.vagNonVegDetails}
                      name={'vagNonVegDetails'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={12}>
                  <CustomFieldWithLabel width={120} label="Declaration regarding Food additive">
                    <TextField
                      fullWidth
                      margin="none"
                      value={formik.values.foodadditive}
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.foodadditive && formik.errors.foodadditive ? true : false}
                      helperText={formik.touched.foodadditive && formik.errors.foodadditive}
                      name={'foodadditive'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={12}>
                  <CustomFieldWithLabel width={120} label="Sample Packing">
                    <TextField
                      fullWidth
                      margin="none"
                      value={formik.values.samplePacking}
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.samplePacking && formik.errors.samplePacking ? true : false}
                      helperText={formik.touched.samplePacking && formik.errors.samplePacking}
                      name={'samplePacking'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={12}>
                  <CustomFieldWithLabel width={120} label="Name And Address Of Importer">
                    <TextField
                      fullWidth
                      margin="none"
                      value={formik.values.nameAndAddressOfImporter}
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.nameAndAddressOfImporter && formik.errors.nameAndAddressOfImporter ? true : false
                      }
                      helperText={formik.touched.nameAndAddressOfImporter && formik.errors.nameAndAddressOfImporter}
                      name={'nameAndAddressOfImporter'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Sample Qty">
                    <TextField
                      fullWidth
                      margin="none"
                      value={formik.values.sampleQty}
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.sampleQty && formik.errors.sampleQty ? true : false}
                      helperText={formik.touched.sampleQty && formik.errors.sampleQty}
                      name={'sampleQty'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Sample Code/Batch">
                    <TextField
                      fullWidth
                      margin="none"
                      value={formik.values.batchNo}
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.batchNo && formik.errors.batchNo ? true : false}
                      helperText={formik.touched.batchNo && formik.errors.batchNo}
                      name={'batchNo'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Mfg Date">
                    <LocalizationProvider
                      dateAdapter={DateAdapter}
                      value={formik.values.mfgDate}
                      error={formik.touched.mfgDate && formik.errors.mfgDate ? true : false}
                      helperText={formik.touched.mfgDate && formik.errors.mfgDate}
                    >
                      <DesktopDatePicker
                        inputFormat="DD/MM/YYYY"
                        onChange={(value) => {
                          const date = value && moment(value).isValid() ? moment(value).toDate() : null;
                          formik.setFieldValue('mfgDate', date);
                          formik.setFieldTouched('mfgDate', true);
                        }}
                        value={formik.values.mfgDate || null}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            margin="none"
                            error={formik.touched.mfgDate && formik.errors.mfgDate ? true : false}
                            helperText={formik.touched.mfgDate && formik.errors.mfgDate}
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </CustomFieldWithLabel>
                </Grid>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Best before buy (Exp Date)">
                    <LocalizationProvider
                      dateAdapter={DateAdapter}
                      value={formik.values.expDate}
                      error={formik.touched.expDate && formik.errors.expDate ? true : false}
                      helperText={formik.touched.expDate && formik.errors.expDate}
                    >
                      <DesktopDatePicker
                        inputFormat="DD/MM/YYYY"
                        value={formik.values.expDate || null}
                        onChange={(value) => {
                          const date = value && moment(value).isValid() ? moment(value).toDate() : null;
                          formik.setFieldValue('expDate', date);
                          formik.setFieldTouched('expDate', true);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            margin="none"
                          />
                        )}
                      />
                    </LocalizationProvider>
                  </CustomFieldWithLabel>
                </Grid>

              </Grid>
              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Country Of Origin">
                    <TextField
                      fullWidth
                      margin="none"
                      value={formik.values.countryOfOrigin}
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.countryOfOrigin && formik.errors.countryOfOrigin ? true : false}
                      helperText={formik.touched.countryOfOrigin && formik.errors.countryOfOrigin}
                      name={'countryOfOrigin'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
                <Grid item xs={6}>
                  <CustomFieldWithLabel width={120} label="Instruction For Usage">
                    <TextField
                      fullWidth
                      margin="none"
                      value={formik.values.instructionForUsed}
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.instructionForUsed && formik.errors.instructionForUsed ? true : false}
                      helperText={formik.touched.instructionForUsed && formik.errors.instructionForUsed}
                      name={'instructionForUsed'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={12}>
                  <CustomFieldWithLabel width={120} label="Test Method">
                    <TextField
                      fullWidth
                      margin="none"
                      value={formik.values.testMethod}
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.testMethod && formik.errors.testMethod ? true : false}
                      helperText={formik.touched.testMethod && formik.errors.testMethod}
                      name={'testMethod'}
                    />
                  </CustomFieldWithLabel>
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ display: 'flex' }} gap={1}>
                <Grid item xs={12}></Grid>
              </Grid>
              {/* {selectedTestGroup?.additionalFields && selectedTestGroup?.additionalFields.length ? (
                <Grid item xs={12}>
                  <Typography style={{ textTransform: 'uppercase' }} fontWeight={'500'} fontSize={14}>
                    Additional Details
                  </Typography>
                  <Divider />
                </Grid>
              ) : null} */}
              {/* {selectedTestGroup?.additionalFields &&
                (selectedTestGroup?.additionalFields || []).map((field) => {
                  return (
                    <Grid item xs={2}>
                      <TextField
                        fullWidth
                        // required
                        label={field.key}
                        value={additionalFields[field.key]}
                        onChange={handleChange}
                        onBlur={formik.handleBlur}
                        // error={formik.touched.rate && formik.errors.rate ? true : false}
                        // helperText={formik.touched.rate && formik.errors.rate}
                        name={`additionalFields.${field.key}`}
                      />
                    </Grid>
                  );
                })} */}
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <Grid container spacing={1} mt={0.5}>
              <Grid item xs={12}>
                <Typography style={{ textTransform: 'uppercase' }} fontWeight={'500'} fontSize={14}>
                  Details of Sampling
                </Typography>
                <Divider />
              </Grid>

              <Grid item xs={6}>
                <CustomFieldWithLabel label="Name of Plant/Section" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.nameOfPlantSection}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.nameOfPlantSection && formik.errors.nameOfPlantSection ? true : false}
                    helperText={formik.touched.nameOfPlantSection && formik.errors.nameOfPlantSection}
                    name={'nameOfPlantSection'}
                  />
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel label=" Condition of Package" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.purposeOfMonitoring}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.purposeOfMonitoring && formik.errors.purposeOfMonitoring ? true : false}
                    helperText={formik.touched.purposeOfMonitoring && formik.errors.purposeOfMonitoring}
                    name={'purposeOfMonitoring'}
                  />
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel label="Emission Source Monitor" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.emissionSourceMonitor}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.emissionSourceMonitor && formik.errors.emissionSourceMonitor ? true : false}
                    helperText={formik.touched.emissionSourceMonitor && formik.errors.emissionSourceMonitor}
                    name={'emissionSourceMonitor'}
                  />
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel label=" Sampling Time" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.productManufacturedSamplingTime}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.productManufacturedSamplingTime && formik.errors.productManufacturedSamplingTime
                        ? true
                        : false
                    }
                    helperText={
                      formik.touched.productManufacturedSamplingTime && formik.errors.productManufacturedSamplingTime
                    }
                    name={'productManufacturedSamplingTime'}
                  />
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel label="Name of Authorized Person" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.nameOfAuthorizedPerson}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.nameOfAuthorizedPerson && formik.errors.nameOfAuthorizedPerson ? true : false}
                    helperText={formik.touched.nameOfAuthorizedPerson && formik.errors.nameOfAuthorizedPerson}
                    name={'nameOfAuthorizedPerson'}
                  />
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel label="Physical Appearance of  sample" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.controlMeasures}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.controlMeasures && formik.errors.controlMeasures ? true : false}
                    helperText={formik.touched.controlMeasures && formik.errors.controlMeasures}
                    name={'controlMeasures'}
                  />
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel label="Address" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.authorizedPersonAddress}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.authorizedPersonAddress && formik.errors.authorizedPersonAddress ? true : false
                    }
                    helperText={formik.touched.authorizedPersonAddress && formik.errors.authorizedPersonAddress}
                    name={'authorizedPersonAddress'}
                  />
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel label="Labelling-Details Sufficient" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.recoveryOfMaterial}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.recoveryOfMaterial && formik.errors.recoveryOfMaterial ? true : false}
                    helperText={formik.touched.recoveryOfMaterial && formik.errors.recoveryOfMaterial}
                    name={'recoveryOfMaterial'}
                  />
                </CustomFieldWithLabel>
              </Grid>

              <Grid item xs={6}>
                <CustomFieldWithLabel label="Seal on container/BIS Seal" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.sealOnContainerBISSeal}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.sealOnContainerBISSeal && formik.errors.sealOnContainerBISSeal ? true : false}
                    helperText={formik.touched.sealOnContainerBISSeal && formik.errors.sealOnContainerBISSeal}
                    name={'sealOnContainerBISSeal'}
                  />
                </CustomFieldWithLabel>
              </Grid>

              <Grid item xs={6}>
                <CustomFieldWithLabel
                  label="If condition of Packing or Sample or details provided in the label is not acceptable,provide reason"
                  width={110}
                >
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.generalSensory}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.generalSensory && formik.errors.generalSensory ? true : false}
                    helperText={formik.touched.generalSensory && formik.errors.generalSensory}
                    name={'generalSensory'}
                  />
                </CustomFieldWithLabel>
              </Grid>

              <Grid item xs={6}>
                <CustomFieldWithLabel label="CHA Regn No." width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.CHSRegnNo}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.CHSRegnNo && formik.errors.CHSRegnNo ? true : false}
                    helperText={formik.touched.CHSRegnNo && formik.errors.CHSRegnNo}
                    name={'CHSRegnNo'}
                  />
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel label="Fugitive Emission" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.fugitiveEmission}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.fugitiveEmission && formik.errors.fugitiveEmission ? true : false}
                    helperText={formik.touched.fugitiveEmission && formik.errors.fugitiveEmission}
                    name={'fugitiveEmission'}
                  />
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel label="CHA G/H Card Holder" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.CHACardHolder}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.CHACardHolder && formik.errors.CHACardHolder ? true : false}
                    helperText={formik.touched.CHACardHolder && formik.errors.CHACardHolder}
                    name={'CHACardHolder'}
                  />
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel label="Stack Temperature" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.stackTemp}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.stackTemp && formik.errors.stackTemp ? true : false}
                    helperText={formik.touched.stackTemp && formik.errors.stackTemp}
                    name={'stackTemp'}
                  />
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel label="Date and Place of collection of sample" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.dateAndPlaceOfCollectionOfSample}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.dateAndPlaceOfCollectionOfSample && formik.errors.dateAndPlaceOfCollectionOfSample
                        ? true
                        : false
                    }
                    helperText={
                      formik.touched.dateAndPlaceOfCollectionOfSample && formik.errors.dateAndPlaceOfCollectionOfSample
                    }
                    name={'dateAndPlaceOfCollectionOfSample'}
                  />
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel label="Ambient Temperature" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.ambientTemperature}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.ambientTemperature && formik.errors.ambientTemperature ? true : false}
                    helperText={formik.touched.ambientTemperature && formik.errors.ambientTemperature}
                    name={'ambientTemperature'}
                  />
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel label="Sr. No." width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.srNo}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.srNo && formik.errors.srNo ? true : false}
                    helperText={formik.touched.srNo && formik.errors.srNo}
                    name={'srNo'}
                  />
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel label="Stack Velocity / IO Signature" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.stackVelocity}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.stackVelocity && formik.errors.stackVelocity ? true : false}
                    helperText={formik.touched.stackVelocity && formik.errors.stackVelocity}
                    name={'stackVelocity'}
                  />
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel label="Seals fixed no." width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.sealsFixedNo}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.sealsFixedNo && formik.errors.sealsFixedNo ? true : false}
                    helperText={formik.touched.sealsFixedNo && formik.errors.sealsFixedNo}
                    name={'sealsFixedNo'}
                  />
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel label="Quantity of emission shoe always Conforms" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.conformsOrNon}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.conformsOrNon && formik.errors.conformsOrNon ? true : false}
                    helperText={formik.touched.conformsOrNon && formik.errors.conformsOrNon}
                    name={'conformsOrNon'}
                  />
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel label="Sample received from" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.sealedCoverByThe}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.sealedCoverByThe && formik.errors.sealedCoverByThe ? true : false}
                    helperText={formik.touched.sealedCoverByThe && formik.errors.sealedCoverByThe}
                    name={'sealedCoverByThe'}
                  />
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel width={110} label="Service  Mode">
                  {/* @ts-ignore: Unreachable code error */}
                  <FormControl
                    margin="dense"
                    fullWidth
                    size="small"
                    required
                    // @ts-ignore: Unreachable code error
                    value={formik.values.serviceMode}
                    error={formik.touched.serviceMode && formik.errors.serviceMode ? true : false}
                    helperText={formik.touched.serviceMode && formik.errors.serviceMode}
                  >
                    <Select
                      labelId="serviceMode-select-label"
                      id="serviceMode-select-helper"
                      name="serviceMode"
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.serviceMode}
                      error={formik.touched.serviceMode && formik.errors.serviceMode ? true : false}
                    >
                      <MenuItem value={'Regular'}>Regular</MenuItem>
                      <MenuItem value={'Courier'}>Courier</MenuItem>
                      <MenuItem value={'Mail'}>Mail</MenuItem>
                    </Select>
                    <FormHelperText>{formik.touched.serviceMode && formik.errors.serviceMode}</FormHelperText>
                  </FormControl>
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel label=" Fee Received " width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.foundTheSampleToBe}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.foundTheSampleToBe && formik.errors.foundTheSampleToBe ? true : false}
                    helperText={formik.touched.foundTheSampleToBe && formik.errors.foundTheSampleToBe}
                    name={'foundTheSampleToBe'}
                  />
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel width={110} label="Sample status">
                  {/* @ts-ignore: Unreachable code error */}
                  <FormControl
                    margin="dense"
                    fullWidth
                    size="small"
                    required
                    // @ts-ignore: Unreachable code error
                    value={formik.values.sampleStatus}
                    error={formik.touched.sampleStatus && formik.errors.sampleStatus ? true : false}
                    helperText={formik.touched.sampleStatus && formik.errors.sampleStatus}
                  >
                    <Select
                      labelId="samplestatus-select-label"
                      id="samplestatus-select-helper"
                      name="samplestatus"
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.sampleStatus}
                      error={formik.touched.sampleStatus && formik.errors.sampleStatus ? true : false}
                    >
                      <MenuItem value={'Process'}>On Process</MenuItem>
                      <MenuItem value={'Delivered'}>Delivered</MenuItem>
                      <MenuItem value={'Completed'}>Completed</MenuItem>
                    </Select>
                    <FormHelperText>{formik.touched.sampleStatus && formik.errors.sampleStatus}</FormHelperText>
                  </FormControl>
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel label="Type of Packing" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.parametersMonitors}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.parametersMonitors && formik.errors.parametersMonitors ? true : false}
                    helperText={formik.touched.parametersMonitors && formik.errors.parametersMonitors}
                    name={'parametersMonitors'}
                  />
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel label="Status reasons" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.statusReasons}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.statusReasons && formik.errors.statusReasons ? true : false}
                    helperText={formik.touched.statusReasons && formik.errors.statusReasons}
                    name={'statusReasons'}
                  />
                </CustomFieldWithLabel>
              </Grid>
            </Grid>

            <Grid container spacing={1} mt={0.5}>
              <Grid item xs={12}>
                <Typography style={{ textTransform: 'uppercase' }} fontWeight={'500'} fontSize={14}>
                  Details for laboratory use
                </Typography>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <CustomFieldWithLabel label="Any other information" width={110}>
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.otherInformation}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.otherInformation && formik.errors.otherInformation ? true : false}
                    helperText={formik.touched.otherInformation && formik.errors.otherInformation}
                    name={'otherInformation'}
                  />
                </CustomFieldWithLabel>
              </Grid>

              <Grid item xs={6}>
                <CustomFieldWithLabel label="Collection By" width={110}>
                  {/* @ts-ignore: Unreachable code error */}
                  <TextField
                    fullWidth
                    margin="none"
                    value={formik.values.collectionBy}
                    onChange={handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.collectionBy && formik.errors.collectionBy ? true : false}
                    helperText={formik.touched.collectionBy && formik.errors.collectionBy}
                    name={'collectionBy'}
                  />
                </CustomFieldWithLabel>
              </Grid>

              <Grid item xs={6}>
                <CustomFieldWithLabel width={110} label="Receive Mode">
                  {/* @ts-ignore: Unreachable code error */}
                  <FormControl
                    margin="dense"
                    fullWidth
                    size="small"
                    required
                    // @ts-ignore: Unreachable code error
                    value={formik.values.sampleReceivedMode}
                    error={formik.touched.sampleReceivedMode && formik.errors.sampleReceivedMode ? true : false}
                    helperText={formik.touched.sampleReceivedMode && formik.errors.sampleReceivedMode}
                  >
                    <Select
                      labelId="sampleReceivedMode-select-label"
                      id="sampleReceivedMode-select-helper"
                      name="sampleReceivedMode"
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.sampleReceivedMode}
                      error={formik.touched.sampleReceivedMode && formik.errors.sampleReceivedMode ? true : false}
                    >
                      <MenuItem value={'Self'}>Self</MenuItem>
                      <MenuItem value={'Courier'}>Courier</MenuItem>
                      <MenuItem value={'Mail'}>Mail</MenuItem>
                    </Select>
                    <FormHelperText>
                      {formik.touched.sampleReceivedMode && formik.errors.sampleReceivedMode}
                    </FormHelperText>
                  </FormControl>
                </CustomFieldWithLabel>
              </Grid>

              <Grid item xs={6}>
                <CustomFieldWithLabel label="HOD" width={110}>
                  {/* @ts-ignore: Unreachable code error */}
                  <FormControl
                    margin="dense"
                    fullWidth
                    size="small"
                    required
                    value={formik.values.hod}
                    error={formik.touched.hod && formik.errors.hod ? true : false}
                    helperText={formik.touched.hod && formik.errors.hod}
                  >
                    <Select
                      labelId="hod-select-label"
                      id="hod-select-helper"
                      name="hod"
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.hod}
                      error={formik.touched.hod && formik.errors.hod ? true : false}
                    >
                      {props.userList
                        .filter((i) => i.role === 'ADMIN' || i.role === 'HOD')
                        .map((item) => (
                          <MenuItem value={item.user._id} key={item._id}>
                            {item.user.name}
                          </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>{formik.touched.hod && formik.errors.hod}</FormHelperText>
                  </FormControl>
                </CustomFieldWithLabel>
              </Grid>

              <Grid item xs={6}>
                <CustomFieldWithLabel label="Authorized Signature 1" width={110}>
                  {/* @ts-ignore: Unreachable code error */}
                  <FormControl
                    margin="dense"
                    fullWidth
                    size="small"
                    required
                    value={formik.values.authorizedSignature1}
                    error={formik.touched.authorizedSignature1 && formik.errors.authorizedSignature1 ? true : false}
                    helperText={formik.touched.authorizedSignature1 && formik.errors.authorizedSignature1}
                  >
                    <Select
                      labelId="authorizedSignature1-select-label"
                      id="authorizedSignature1-select-helper"
                      name="authorizedSignature1"
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.authorizedSignature1}
                      error={formik.touched.authorizedSignature1 && formik.errors.authorizedSignature1 ? true : false}
                    >
                      {props.userList
                        .filter((i) => i.role === 'ADMIN' || i.role === 'HOD')
                        .map((item) => (
                          <MenuItem value={item.user._id} key={item._id}>
                            {item.user.name}
                          </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                      {formik.touched.authorizedSignature1 && formik.errors.authorizedSignature1}
                    </FormHelperText>
                  </FormControl>
                </CustomFieldWithLabel>
              </Grid>
              <Grid item xs={6}>
                <CustomFieldWithLabel label="Authorized Signature 2" width={110}>
                  {/* @ts-ignore: Unreachable code error */}
                  <FormControl
                    margin="dense"
                    fullWidth
                    size="small"
                    required
                    value={formik.values.authorizedSignature2}
                    error={formik.touched.authorizedSignature2 && formik.errors.authorizedSignature2 ? true : false}
                    helperText={formik.touched.authorizedSignature2 && formik.errors.authorizedSignature2}
                  >
                    <Select
                      labelId="authorizedSignature2-select-label"
                      id="authorizedSignature2-select-helper"
                      name="authorizedSignature2"
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.authorizedSignature2}
                      error={formik.touched.authorizedSignature2 && formik.errors.authorizedSignature2 ? true : false}
                    >
                      {props.userList
                        .filter((i) => i.role === 'ADMIN' || i.role === 'HOD')
                        .map((item) => (
                          <MenuItem value={item.user._id} key={item._id}>
                            {item.user.name}
                          </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                      {formik.touched.authorizedSignature2 && formik.errors.authorizedSignature2}
                    </FormHelperText>
                  </FormControl>
                </CustomFieldWithLabel>
              </Grid>

              <Grid item xs={6}>
                <CustomFieldWithLabel label="Reviewed By" width={110}>
                  {/* @ts-ignore: Unreachable code error */}
                  <FormControl
                    margin="dense"
                    fullWidth
                    size="small"
                    required
                    value={formik.values.reviewedBy}
                    error={formik.touched.reviewedBy && formik.errors.reviewedBy ? true : false}
                    helperText={formik.touched.reviewedBy && formik.errors.reviewedBy}
                  >
                    <Select
                      labelId="reviewedBy-select-label"
                      id="reviewedBy-select-helper"
                      name="reviewedBy"
                      onChange={handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.reviewedBy}
                      error={formik.touched.reviewedBy && formik.errors.reviewedBy ? true : false}
                    >
                      {props.userList.map((item) => (
                        <MenuItem value={item.user._id} key={item._id}>
                          {item.user.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{formik.touched.reviewedBy && formik.errors.reviewedBy}</FormHelperText>
                  </FormControl>
                </CustomFieldWithLabel>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Box display={'flex'} gap={2}>
              <Box flex={1}>
                <CustomFieldWithLabel width={120} label="Parameters">
                  {/* @ts-ignore */}
                  <ReactSelect
                    isMulti
                    closeMenuOnSelect={false}
                    components={animatedComponents}
                    value={(formik.values.parameters || []).map((i: any) => ({ value: i.name, label: i.name }))}
                    onChange={(value) => {
                      const selectedOptions = Array.isArray(value) ? value : [];
                      const availableParameters = selectedMaster?.parameters?.length
                        ? selectedMaster.parameters
                        : formik.values.parameters || [];
                      const selectedParameters = availableParameters.filter((i: any) =>
                        selectedOptions.map((option: any) => option.value).includes(i.name)
                      );
                      formik.setFieldValue('parameters', selectedParameters);
                    }}
                    options={(selectedMaster?.parameters || formik.values.parameters || []).map((i: any) => ({
                      value: i.name,
                      label: i.name,
                    }))}
                  />
                </CustomFieldWithLabel>
                <div>{formik.errors.parameters && <div style={{ color: 'red' }}>{formik.errors.parameters}</div>}</div>
              </Box>
              <div>
                <Button variant="contained" onClick={handleSelectAllParameter}>
                  Select All Parameters
                </Button>
              </div>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography style={{ textTransform: 'uppercase' }} fontWeight={'500'} fontSize={14}>
              Billing Details
            </Typography>
            <Divider />
          </Grid>
          <Grid item xs={2}>
            <TextField
              fullWidth
              required
              label={'Charges (Rate)'}
              type="number"
              value={formik.values.rate}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.rate && formik.errors.rate ? true : false}
              helperText={formik.touched.rate && formik.errors.rate}
              name={'rate'}
            />
            <TextField
              fullWidth
              required
              label={'Discount %'}
              value={formik.values.discount}
              type="number"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.discount && formik.errors.discount ? true : false}
              helperText={formik.touched.discount && formik.errors.discount}
              name={'discount'}
            />
            <Box display={'flex'} gap={1}>
              <TextField
                fullWidth
                required
                type="number"
                style={{ flex: 0.4 }}
                label={'GST %'}
                value={formik.values.gst}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.gst && formik.errors.gst ? true : false}
                helperText={formik.touched.gst && formik.errors.gst}
                name={'gst'}
              />
              <Box display={'flex'} style={{ flex: 0.6 }} alignItems={'center'}>
                <FormControl margin="dense" fullWidth size="small" required>
                  {/* <InputLabel id="department-select-label">Department</InputLabel> */}
                  <Select
                    id="demo-simple-select-helper"
                    name="igst"
                    onChange={(e) => {
                      formik.setFieldValue('igst', e.target.value === 'true');
                    }}
                    value={formik.values.igst ? 'true' : 'false'}
                  >
                    <MenuItem value={'false'}>CGST / SGST</MenuItem>
                    <MenuItem value={'true'}>IGST</MenuItem>
                  </Select>
                </FormControl>
                {/* Render checkbox for igst formik field */}
                {/* <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.igst}
                      onChange={formik.handleChange}
                      name="igst"
                      color="primary"
                      size="small"
                    />
                  }
                  label="IGST"
                /> */}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={2}>
            <TextField
              fullWidth
              required
              type="number"
              label={'Paid Amount'}
              value={formik.values.paidAmount}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.paidAmount && formik.errors.paidAmount ? true : false}
              helperText={formik.touched.paidAmount && formik.errors.paidAmount}
              name={'paidAmount'}
            />
            <TextField fullWidth required disabled label={'Total'} value={total} />
            <TextField fullWidth required disabled label={'Remaining Total'} value={remainingTotal} />
          </Grid>

          <Grid item xs={3}>
            <LocalizationProvider dateAdapter={DateAdapter}>
              <DesktopDatePicker
                label="Payment Due Date"
                inputFormat="DD/MM/YYYY"
                onChange={(value) => {
                  formik.setFieldValue('paymentDueDate', moment(value).toDate());
                  formik.setFieldTouched('paymentDueDate', true);
                }}
                value={formik.values.paymentDueDate}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    required
                    error={formik.touched.paymentDueDate && formik.errors.paymentDueDate ? true : false}
                    helperText={formik.touched.paymentDueDate && formik.errors.paymentDueDate}
                  />
                )}
              />
            </LocalizationProvider>
            <TextField
              fullWidth
              required
              label={'SAC Code'}
              value={formik.values.sac}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.sac && formik.errors.sac ? true : false}
              helperText={formik.touched.sac && formik.errors.sac}
              name={'sac'}
            />
          </Grid>
        </Grid>

        <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} mt={2}>
          <Box>
            <Button disabled={formik.isSubmitting} variant="outlined" onClick={() => navigate(`/laboratory/samples`)}>
              Back
            </Button>
          </Box>
          <Box ml={1}>
            <Button variant="contained" disabled={formik.isSubmitting} onClick={() => formik.handleSubmit()}>
              {sampleId ? 'Update' : 'Save'}
            </Button>
          </Box>
        </Box>
      </Box>

      <SearchDialog
        isVisible={customerSearchModal}
        title={'Search customer'}
        body={
          <Box>
            <TextField
              fullWidth
              label={'Search'}
              value={customerSearchQuery}
              autoFocus
              onChange={(e) => setCustomerSearchQuery(e.target.value)}
            />
            {!props.searchCustomerList.length && (
              <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
                <Typography variant="subtitle1" fontSize={14} pl={1}>
                  Not found
                </Typography>
              </Box>
            )}
            {props.searchCustomerList.map((c) => (
              <Box
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setSelectedCustomer(c);
                  setCustomerSearchModal(false);
                  setCustomerSearchQuery('');
                  formik.setFieldValue('customer', c._id);
                }}
              >
                <Box display={'flex'} pt={1} pb={1} alignItems={'center'} justifyContent={'space-between'}>
                  <Typography variant="h6" color={'#000'} fontSize={14}>
                    {c.name}
                  </Typography>
                  <Typography variant="subtitle1" color={'#000'} fontSize={14} pl={1}>
                    {c.email}
                  </Typography>
                  <Typography variant="subtitle1" color={'#000'} fontSize={14} pl={1}>
                    {c.country} {c.phone}
                  </Typography>
                </Box>
                <Divider />
              </Box>
            ))}
          </Box>
        }
        onAction={() => { }}
        onClose={() => {
          setCustomerSearchModal(false);
          setCustomerSearchQuery('');
        }}
        hideButtons={true}
      />

      <SearchDialog
        isVisible={testGroupSearchModal}
        title={'Search Test Group'}
        body={
          <Box>
            <TextField
              fullWidth
              label={'Search'}
              value={testGroupSearchQuery}
              autoFocus
              onChange={(e) => setTestGroupSearchQuery(e.target.value)}
            />
            {!props.masterList.length && (
              <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
                <Typography variant="subtitle1" fontSize={14} pl={1}>
                  Not found
                </Typography>
              </Box>
            )}
            {props.testGroupsList.map((m) => (
              <Box
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setSelectedTestGroup(m);
                  setTestGroupSearchModal(false);
                  setTestGroupSearchQuery('');
                  formik.setFieldValue('testGroup', m._id);
                }}
              >
                <Box display={'flex'} pt={1} pb={1} alignItems={'center'} justifyContent={'space-between'}>
                  <Typography variant="h6" color={'#000'} fontSize={14}>
                    {m.name}
                  </Typography>
                  <Typography variant="subtitle1" color={'#000'} fontSize={14} pl={1}>
                    {m.prefix}
                  </Typography>
                </Box>
                <Divider />
              </Box>
            ))}
          </Box>
        }
        onAction={() => { }}
        onClose={() => {
          setTestGroupSearchModal(false);
          setTestGroupSearchQuery('');
        }}
        hideButtons={true}
      />

      <SearchDialog
        isVisible={masterSearchModal}
        title={'Search Master'}
        body={
          <Box>
            <TextField
              fullWidth
              label={'Search'}
              value={masterSearchQuery}
              autoFocus
              onChange={(e) => setMasterSearchQuery(e.target.value)}
            />
            {!props.masterList.length && (
              <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
                <Typography variant="subtitle1" fontSize={14} pl={1}>
                  Not found
                </Typography>
              </Box>
            )}
            {props.masterList.map((m) => (
              <Box
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setSelectedMaster(m);
                  setMasterSearchModal(false);
                  setMasterSearchQuery('');
                  formik.setFieldValue('marking', m.marking || '');
                  formik.setFieldValue('name', m.sampleName);
                  formik.setFieldValue('genericName', m.genericName);
                  formik.setFieldValue('testMethod', m.testingMethod);
                  formik.setFieldValue('parameters', []);
                }}
              >
                <Box display={'flex'} pt={1} pb={1} alignItems={'center'} justifyContent={'space-between'}>
                  <Typography variant="h6" color={'#000'} fontSize={14}>
                    {m.sampleName}
                  </Typography>
                  <Typography variant="subtitle1" color={'#000'} fontSize={14} pl={1}>
                    {m.genericName}
                  </Typography>
                  <Typography variant="subtitle1" color={'#000'} fontSize={14} pl={1}>
                    {m.masterId}
                  </Typography>
                </Box>
                <Divider />
              </Box>
            ))}
          </Box>
        }
        onAction={() => { }}
        onClose={() => {
          setMasterSearchModal(false);
          setMasterSearchQuery('');
        }}
        hideButtons={true}
      />

      <SearchDialog
        isVisible={referenceSearchModal}
        title={'Search Reference'}
        body={
          <Box>
            <TextField
              fullWidth
              label={'Search'}
              value={referenceSearchQuery}
              autoFocus
              onChange={(e) => setReferenceSearchQuery(e.target.value)}
            />
            {!props?.referenceList?.length && (
              <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
                <Typography variant="subtitle1" fontSize={14} pl={1}>
                  Not found
                </Typography>
              </Box>
            )}
            {props?.referenceList?.map((m) => (
              <Box
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setSelectedReference(m);
                  setReferenceSearchModal(false);
                  setReferenceSearchQuery('');
                  formik.setFieldValue('reference', m._id);
                }}
              >
                <Box display={'flex'} pt={1} pb={1} alignItems={'center'} justifyContent={'space-between'}>
                  <Typography variant="h6" color={'#000'} fontSize={14}>
                    {m.name}
                  </Typography>
                </Box>
                <Divider />
              </Box>
            ))}
          </Box>
        }
        onAction={() => { }}
        onClose={() => {
          setReferenceSearchModal(false);
          setReferenceSearchQuery('');
        }}
        hideButtons={true}
      />
    </Box>
  );
}

const mapStateToProps = (state: IReducer) => ({
  user: state.auth.user,
  laboratory: state.laboratory.current,
  userList: Array.isArray(state.laboratory.users)
    ? state.laboratory.users.filter((item) => item?.user?._id)
    : [],
  department: Array.isArray(state.department.list) ? state.department.list : [],
  searchCustomerList: Array.isArray(state.laboratory.searchCustomer) ? state.laboratory.searchCustomer : [],
  masterList: Array.isArray(state.laboratory.masters) ? state.laboratory.masters : [],
  testGroupsList: Array.isArray(state.laboratory.testGroups) ? state.laboratory.testGroups : [],
  referenceList: Array.isArray(state.laboratory.reference) ? state.laboratory.reference : [],
  sampleDetails: state.laboratory.sampleDetails
    ? {
        ...state.laboratory.sampleDetails,
        parameters: Array.isArray(state.laboratory.sampleDetails.parameters)
          ? state.laboratory.sampleDetails.parameters
          : [],
      }
    : null,
});

function mapDispatchToProps(dispatch: any) {
  return {
    ...bindActionCreators(
      {
        searchCustomer,
        searchMasterList,
        searchTestGroupList,
        getUserList,
        createSample,
        searchReference,
        getSampleById,
        updateSample,
      },
      dispatch
    ),
  };
}

export const PersistSampleKSZ = connect(mapStateToProps, mapDispatchToProps)(_PersistSampleKSZ);
